import { ApiResponse } from "../context/VolunteersHighlightContext/constants";
import {
	UpdateVolunteersExperienceRequest,
	VolunteersExperienceFormData,
	VolunteersExperienceGetApiResponse,
	VolunteersExperienceListApiResponse,
} from "../context/VolunteersExperienceContext/constants";
import { axiosInstance } from "../utils/axiosInstance";
import { toCrLf } from "../utils/lineBreaks";

const ENDPOINTS = {
	VOLUNTEERS_EXPERIENCE: "/cms/volunteers-experience",
};

const LOCALIZABLE_PREFIX = "volunteersExperienceLocalizable";

interface LocalizableFormFields {
	volunteersExperienceLocalizableId?: string;
	culture: string;
	description?: string;
	title?: string;
	company?: string;
}

// The backend names the stored picture after the uploaded file; the upload time keeps a re-uploaded file with the same
// name from keeping the old picture's CDN address (and its cached image).
const toUploadFileName = (file: File) => `${Date.now()}-${file.name}`;

const appendText = (formData: FormData, key: string, value?: string) => {
	if (value !== undefined) {
		formData.append(key, toCrLf(value.trim()));
	}
};

const appendPicture = (formData: FormData, picture?: File | null) => {
	if (picture) {
		formData.append("picture", picture, toUploadFileName(picture));
	}
};

const appendLocalizable = (formData: FormData, index: number, item: LocalizableFormFields) => {
	const prefix = `${LOCALIZABLE_PREFIX}[${index}]`;
	if (item.volunteersExperienceLocalizableId) {
		formData.append(`${prefix}.volunteersExperienceLocalizableId`, item.volunteersExperienceLocalizableId);
	}
	formData.append(`${prefix}.culture`, item.culture);
	appendText(formData, `${prefix}.description`, item.description);
	appendText(formData, `${prefix}.title`, item.title);
	appendText(formData, `${prefix}.company`, item.company);
};

// Empty optional texts (title, company) are left out of a new record.
export const buildAddVolunteersExperienceFormData = (data: VolunteersExperienceFormData): FormData => {
	const formData = new FormData();

	formData.append("isActive", String(data.isActive));
	if (data.displayOrder !== undefined) {
		formData.append("displayOrder", String(data.displayOrder));
	}
	appendText(formData, "name", data.name);
	appendText(formData, "surname", data.surname);
	appendPicture(formData, data.picture);

	data.volunteersExperienceLocalizable.forEach((item, index) =>
		appendLocalizable(formData, index, {
			culture: item.culture,
			description: item.description,
			title: item.title.trim() || undefined,
			company: item.company.trim() || undefined,
		})
	);

	return formData;
};

// Only the changed fields are sent; a picture that is not replaced keeps the stored one.
export const buildUpdateVolunteersExperienceFormData = (request: UpdateVolunteersExperienceRequest): FormData => {
	const formData = new FormData();

	formData.append("experienceId", request.experienceId);
	if (request.isActive !== undefined) {
		formData.append("isActive", String(request.isActive));
	}
	if (typeof request.displayOrder === "number") {
		formData.append("displayOrder", String(request.displayOrder));
	}
	appendText(formData, "name", request.name);
	appendText(formData, "surname", request.surname);
	appendPicture(formData, request.picture);

	request.volunteersExperienceLocalizable?.forEach((item, index) => appendLocalizable(formData, index, item));

	return formData;
};

export class VolunteersExperienceService {
	static async getVolunteersExperienceList(): Promise<VolunteersExperienceListApiResponse> {
		const response = await axiosInstance.get(ENDPOINTS.VOLUNTEERS_EXPERIENCE);
		return response.data;
	}

	static async getVolunteersExperienceDetails(experienceId: string): Promise<VolunteersExperienceGetApiResponse> {
		const response = await axiosInstance.get(`${ENDPOINTS.VOLUNTEERS_EXPERIENCE}/${experienceId}`);
		return response.data;
	}

	static async addVolunteersExperience(data: VolunteersExperienceFormData): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.post(ENDPOINTS.VOLUNTEERS_EXPERIENCE, buildAddVolunteersExperienceFormData(data));
		return response.data;
	}

	static async updateVolunteersExperience(request: UpdateVolunteersExperienceRequest): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.patch(ENDPOINTS.VOLUNTEERS_EXPERIENCE, buildUpdateVolunteersExperienceFormData(request));
		return response.data;
	}

	static async removeVolunteersExperience(experienceId: string): Promise<ApiResponse<unknown>> {
		const response = await axiosInstance.delete(`${ENDPOINTS.VOLUNTEERS_EXPERIENCE}/${experienceId}`);

		if (response.status === 204) {
			return { result: null, error: null, isSuccessful: true };
		}

		return response.data;
	}
}
